/**
 * Wave chat-instant · Zustand store pra UX WhatsApp/iMessage-style.
 *
 * SEPARADO de:
 *   - `chat.store.ts` (Wave chat-mvp legacy — usa /v1/messages/chat/*)
 *   - `messaging.*`   (inbox formal)
 *
 * Responsabilidades:
 *   - Panel state (lista lateral de conversations aberta/fechada)
 *   - Window state (uma janela por conversation aberta)
 *   - Cache local de conversations + messages (otimismo no envio)
 *   - Presence map (peer online, typing, last seen)
 *   - Unread total agregado (badge do launcher)
 *
 * NÃO faz polling sozinho — quem decide polling é o componente que
 * monta. O store só guarda estado.
 */

import { create } from 'zustand';
import type {
  ChatInstantConversation,
  ChatInstantMessage,
  ChatInstantPresence,
} from '@/api/chat-instant';

// ── Tipos ────────────────────────────────────────────────────────────

export interface ChatInstantWindow {
  conversationId: string;
  /** Aberto = janela visível. Fechado = não monta. Mobile só permite 1. */
  open: boolean;
  /** Minimizado = só chip na bar inferior (desktop only). */
  minimized: boolean;
  /**
   * Wave chat-pills · 2026-05-21
   * Última atividade na conv (ms epoch). Bumpa em send/receive.
   * Usado pra TTL de 30min do pill persistente. NULL = nunca tocado
   * pelos hooks de pill (legacy openWindow direto).
   */
  lastActivityAt?: number;
  /** Unread count exclusivo do pill (badge na borda). */
  unreadCount?: number;
  /**
   * Se o user dispensou manualmente o pill (X). Nunca reaparece até
   * uma NOVA mensagem (incoming ou outgoing) genuína.
   */
  pillDismissed?: boolean;
}

/** TTL antes de auto-prune de um pill silenciosamente. */
export const CHAT_PILL_TTL_MS = 30 * 60 * 1000;
/** Máximo de pills visíveis horizontalmente (resto vira "+N outras"). */
export const CHAT_PILL_VISIBLE_LIMIT = 3;

/**
 * View stack do ChatPanel. JP pediu: clicar numa conversation NÃO deve
 * abrir uma janela separada por trás do panel — deve substituir a lista
 * pela thread INLINE no mesmo panel (com botão back). Mantemos o estado
 * stack-like aqui pra que o backdrop, animação e gestos do panel
 * funcionem como uma unidade só.
 */
export type ChatPanelView = 'list' | 'thread';

interface ChatInstantStoreState {
  // Panel (drawer lateral com lista de conversations)
  panelOpen: boolean;

  /** Qual view do panel está visível: lista de convs OU thread inline. */
  panelView: ChatPanelView;
  /** Conversation ativa quando panelView==='thread'. */
  panelActiveConversationId: string | null;

  // Windows abertas. Key = conversationId.
  // (Mantido pra compat com floating ChatWindow legacy + tests. Não é
  // mais usado pelo fluxo principal do panel — preferimos thread inline.)
  windows: Map<string, ChatInstantWindow>;

  // Cache de conversations (servidor source of truth — store é mirror)
  conversations: ChatInstantConversation[];

  // Mensagens por conversation. Append-only durante sessão; recarregadas
  // quando a window abre ou polling roda.
  messagesByConv: Map<string, ChatInstantMessage[]>;

  // Presence. Key = userId.
  presence: Map<string, ChatInstantPresence>;

  // ─── Actions ───────────────────────────────────────────────────────

  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;

  /**
   * Abre a thread INLINE no mesmo panel (substitui a lista). Se o panel
   * estiver fechado, abre o panel já direto na thread. JP pediu:
   * "should be INLINE in the same panel (substituting the list with the
   * thread + a back button)".
   */
  openThreadInPanel: (conversationId: string) => void;
  /** Volta da thread pra lista — botão back do header do panel. */
  backToList: () => void;

  openWindow: (conversationId: string) => void;
  closeWindow: (conversationId: string) => void;
  minimizeWindow: (conversationId: string) => void;
  restoreWindow: (conversationId: string) => void;

  /**
   * Wave chat-pills · 2026-05-21
   * Persistent minimized pills (Messenger-style chat heads).
   *
   * `pinConversation` é o ponto de entrada — chamado SEMPRE que houve
   * atividade genuína (user mandou ou recebeu uma msg). Cria a entry
   * em `windows` (state='minimized') OU bumpa lastActivityAt se já
   * existir. Limpa `pillDismissed` automaticamente — atividade nova
   * "ressuscita" o pill.
   *
   *   opts.incomingUnread — true se for msg recebida E o panel não tá
   *   olhando essa conv. Incrementa unread badge.
   */
  pinConversation: (
    conversationId: string,
    opts?: { incomingUnread?: boolean },
  ) => void;

  /**
   * Remove o pill imediatamente.
   *  - manual=true (X clicado pelo user): marca `pillDismissed` pra que
   *    auto-pin de polling NÃO ressuscite enquanto a TTL atual valer.
   *    (Próxima msg genuína vai limpar a flag via pinConversation.)
   *  - manual=false (silent prune): remove a entry totalmente.
   */
  unpinConversation: (conversationId: string, manual?: boolean) => void;

  /** Atualiza lastActivityAt sem mudar mais nada (no-op se ñ existir). */
  bumpActivity: (conversationId: string) => void;

  /** Zera o unread count do pill — chamado quando user abre a thread. */
  markPillRead: (conversationId: string) => void;

  /**
   * Tick periódico (a cada 60s) — remove silenciosamente entries cuja
   * lastActivityAt passou da TTL. Não notifica.
   */
  pruneInactivePills: () => void;

  setConversations: (list: ChatInstantConversation[]) => void;
  upsertConversation: (conv: ChatInstantConversation) => void;
  decrementUnread: (conversationId: string) => void;

  setMessages: (conversationId: string, msgs: ChatInstantMessage[]) => void;
  appendMessage: (conversationId: string, msg: ChatInstantMessage) => void;
  removeMessage: (conversationId: string, messageId: string) => void;

  /** Atualiza reactions de UMA mensagem específica (mutação local). */
  patchReactions: (
    conversationId: string,
    messageId: string,
    fn: (current: ChatInstantMessage['reactions']) => ChatInstantMessage['reactions'],
  ) => void;

  setPresence: (map: Record<string, ChatInstantPresence>) => void;
}

// ── Store ────────────────────────────────────────────────────────────

export const useChatInstantStore = create<ChatInstantStoreState>()((set) => ({
  panelOpen: false,
  panelView: 'list',
  panelActiveConversationId: null,
  windows: new Map(),
  conversations: [],
  messagesByConv: new Map(),
  presence: new Map(),

  openPanel() {
    // Sempre que o panel é aberto pelo botão da TopBar, queremos voltar
    // pra lista — clicar no launcher é "ver minhas conversas", não
    // "continuar de onde parei". Se o caller quer abrir já na thread,
    // que chame openThreadInPanel.
    set({ panelOpen: true, panelView: 'list', panelActiveConversationId: null });
  },
  closePanel() {
    // Fecha o panel mas preserva panelView pra fade-out continuar fluido.
    set({ panelOpen: false });
  },
  togglePanel() {
    set((s) =>
      s.panelOpen
        ? { panelOpen: false }
        : { panelOpen: true, panelView: 'list', panelActiveConversationId: null },
    );
  },

  openThreadInPanel(conversationId) {
    set({
      panelOpen: true,
      panelView: 'thread',
      panelActiveConversationId: conversationId,
    });
  },

  backToList() {
    set({ panelView: 'list', panelActiveConversationId: null });
  },

  openWindow(conversationId) {
    set((s) => {
      const next = new Map(s.windows);
      const existing = next.get(conversationId);
      if (existing) {
        next.set(conversationId, { ...existing, open: true, minimized: false });
      } else {
        next.set(conversationId, { conversationId, open: true, minimized: false });
      }
      return { windows: next };
    });
  },

  closeWindow(conversationId) {
    set((s) => {
      const next = new Map(s.windows);
      next.delete(conversationId);
      return { windows: next };
    });
  },

  minimizeWindow(conversationId) {
    set((s) => {
      const next = new Map(s.windows);
      const w = next.get(conversationId);
      if (w) next.set(conversationId, { ...w, minimized: true, open: false });
      return { windows: next };
    });
  },

  restoreWindow(conversationId) {
    set((s) => {
      const next = new Map(s.windows);
      const w = next.get(conversationId);
      if (w) next.set(conversationId, { ...w, minimized: false, open: true });
      return { windows: next };
    });
  },

  pinConversation(conversationId, opts) {
    set((s) => {
      const next = new Map(s.windows);
      const existing = next.get(conversationId);
      const now = Date.now();
      const incomingUnread = !!opts?.incomingUnread;
      // Se a thread atual no panel é essa conv E o panel tá aberto, NÃO
      // incrementa unread — o user já tá olhando.
      const userIsLookingHere =
        s.panelOpen &&
        s.panelView === 'thread' &&
        s.panelActiveConversationId === conversationId;
      const addUnread = incomingUnread && !userIsLookingHere ? 1 : 0;

      if (existing) {
        next.set(conversationId, {
          ...existing,
          lastActivityAt: now,
          // Atividade nova "ressuscita" o pill — limpa dismissed.
          pillDismissed: false,
          unreadCount: userIsLookingHere
            ? 0
            : (existing.unreadCount ?? 0) + addUnread,
        });
      } else {
        next.set(conversationId, {
          conversationId,
          open: false,
          minimized: true,
          lastActivityAt: now,
          pillDismissed: false,
          unreadCount: addUnread,
        });
      }
      return { windows: next };
    });
  },

  unpinConversation(conversationId, manual = false) {
    set((s) => {
      const next = new Map(s.windows);
      if (!next.has(conversationId)) return s;
      if (manual) {
        // Marca como dismissed mas mantém entry pra "não ressuscitar"
        // até a próxima atividade real. Não precisa segurar muito tempo
        // porque pruneInactivePills vai limpar via TTL anyway.
        const w = next.get(conversationId)!;
        next.set(conversationId, {
          ...w,
          pillDismissed: true,
          unreadCount: 0,
        });
      } else {
        next.delete(conversationId);
      }
      return { windows: next };
    });
  },

  bumpActivity(conversationId) {
    set((s) => {
      const w = s.windows.get(conversationId);
      if (!w) return s;
      const next = new Map(s.windows);
      next.set(conversationId, { ...w, lastActivityAt: Date.now() });
      return { windows: next };
    });
  },

  markPillRead(conversationId) {
    set((s) => {
      const w = s.windows.get(conversationId);
      if (!w || !w.unreadCount) return s;
      const next = new Map(s.windows);
      next.set(conversationId, { ...w, unreadCount: 0 });
      return { windows: next };
    });
  },

  pruneInactivePills() {
    set((s) => {
      const now = Date.now();
      let changed = false;
      const next = new Map(s.windows);
      for (const [id, w] of s.windows.entries()) {
        // Só prunamos entries que têm lastActivityAt definido (criadas
        // pelo fluxo pill). Entries legacy (sem lastActivityAt) ficam.
        if (typeof w.lastActivityAt !== 'number') continue;
        if (now - w.lastActivityAt > CHAT_PILL_TTL_MS) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? { windows: next } : s;
    });
  },

  setConversations(list) {
    set({ conversations: list });
  },

  upsertConversation(conv) {
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === conv.id);
      if (idx === -1) {
        return { conversations: [conv, ...s.conversations] };
      }
      const next = [...s.conversations];
      next[idx] = conv;
      return { conversations: next };
    });
  },

  decrementUnread(conversationId) {
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0 } : c,
      ),
    }));
  },

  setMessages(conversationId, msgs) {
    set((s) => {
      const next = new Map(s.messagesByConv);
      next.set(conversationId, msgs);
      return { messagesByConv: next };
    });
  },

  appendMessage(conversationId, msg) {
    set((s) => {
      const next = new Map(s.messagesByConv);
      const current = next.get(conversationId) ?? [];
      // Dedup por id — polling pode trazer mensagens já apendidas.
      if (current.some((m) => m.id === msg.id)) return s;
      next.set(conversationId, [...current, msg]);
      return { messagesByConv: next };
    });
  },

  removeMessage(conversationId, messageId) {
    set((s) => {
      const next = new Map(s.messagesByConv);
      const current = next.get(conversationId) ?? [];
      next.set(
        conversationId,
        current.filter((m) => m.id !== messageId),
      );
      return { messagesByConv: next };
    });
  },

  patchReactions(conversationId, messageId, fn) {
    set((s) => {
      const next = new Map(s.messagesByConv);
      const current = next.get(conversationId) ?? [];
      next.set(
        conversationId,
        current.map((m) =>
          m.id === messageId ? { ...m, reactions: fn(m.reactions) } : m,
        ),
      );
      return { messagesByConv: next };
    });
  },

  setPresence(map) {
    set((s) => {
      const next = new Map(s.presence);
      for (const [uid, p] of Object.entries(map)) {
        next.set(uid, p);
      }
      return { presence: next };
    });
  },
}));

// ── Derived selectors (helpers) ──────────────────────────────────────

/** Soma de unreads em todas as conversations — pra badge do launcher. */
export function selectUnreadTotal(s: ChatInstantStoreState): number {
  let total = 0;
  for (const c of s.conversations) total += c.unreadCount;
  return total;
}

// ── Pill selectors (Wave chat-pills · 2026-05-21) ────────────────────

export interface ChatPillEntry {
  conversationId: string;
  lastActivityAt: number;
  unreadCount: number;
}

/**
 * Pills ativos = entries criadas pelo fluxo pill (lastActivityAt
 * definido), ainda dentro da TTL, não dispensadas manualmente. Ordenadas
 * por recência (mais recente primeiro). Limitado ao topo CHAT_PILL_VISIBLE_LIMIT.
 */
export function selectActivePills(s: ChatInstantStoreState): ChatPillEntry[] {
  const now = Date.now();
  const active: ChatPillEntry[] = [];
  for (const w of s.windows.values()) {
    if (typeof w.lastActivityAt !== 'number') continue;
    if (w.pillDismissed) continue;
    if (now - w.lastActivityAt > CHAT_PILL_TTL_MS) continue;
    active.push({
      conversationId: w.conversationId,
      lastActivityAt: w.lastActivityAt,
      unreadCount: w.unreadCount ?? 0,
    });
  }
  // Mais recente primeiro.
  active.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
  return active.slice(0, CHAT_PILL_VISIBLE_LIMIT);
}

/** Quantos pills ATIVOS estão escondidos além do limite visível. */
export function selectPillOverflowCount(s: ChatInstantStoreState): number {
  const now = Date.now();
  let total = 0;
  for (const w of s.windows.values()) {
    if (typeof w.lastActivityAt !== 'number') continue;
    if (w.pillDismissed) continue;
    if (now - w.lastActivityAt > CHAT_PILL_TTL_MS) continue;
    total += 1;
  }
  return Math.max(0, total - CHAT_PILL_VISIBLE_LIMIT);
}
